﻿import { KPICard, KPIThresholds } from "@/shared/components/KPICard";
import { Box, Grid2, Typography } from "@mui/material";
import {
	actualTons,
	actualTonsTooltip,
	productivityDuringUptime,
	targetTons,
	targetTonsTooltip,
} from "../models/KpiData";
import type { KPIData } from "../models/KpiData";

const formatValue = (
	value: number | null | undefined,
	decimals: number,
	unit: string,
): string => {
	if (value === null || value === undefined) return "-";
	return `${parseFloat(value.toFixed(decimals))} ${unit}`;
};

type CardKey = "tph" | "uptime" | "pushrate" | "productivity";

type CardDef = {
	key: CardKey;
	title: string;
	value: number | null;
	unit: string;
	percentage: number | null;
	subtitle: string;
	tooltip?: string;
	thresholds: KPIThresholds;
	details: { label: string; value: string; tooltip?: string }[];
};

const n = (v: number | null | undefined): number | null => v ?? null;

const buildCards = (data: KPIData): CardDef[] => [
	{
		key: "tph",
		title: "TPH",
		value: n(data.TPH),
		unit: "TPH",
		percentage: n(data.TPHPct),
		subtitle: "Actual Avg",
		tooltip: "Rolled Tons / Hours Actual",
		thresholds: {
			red: data.TPHRed,
			orange: data.TPHOrange,
			yellow: data.TPHYellow,
			greenSoft: data.TPHGreenSoft,
			greenNormal: data.TPHGreenNormal,
			greenHard: data.TPHGreenHard,
		},
		details: [
			{
				label: "Target",
				value: formatValue(data.TPHTarget, 2, "TPH"),
				tooltip: "Section catalog target TPH.",
			},
			{
				label: "TPH Avg Last Hour",
				value: formatValue(data.TPHAvgLastHour, 2, "TPH"),
				tooltip: "Average TPH based on tons rolled in the last 60 minutes.",
			},
		],
	},
	{
		key: "uptime",
		title: "Uptime",
		value: n(data.Uptime),
		unit: "Hrs.",
		percentage: n(data.UptimePct),
		subtitle: "Uptime - Mills On",
		tooltip: "Hours Actual - Delay Hours",
		thresholds: {
			red: data.UptimeRed,
			orange: data.UptimeOrange,
			yellow: data.UptimeYellow,
			greenSoft: data.UptimeGreenSoft,
			greenNormal: data.UptimeGreenNormal,
			greenHard: data.UptimeGreenHard,
		},
		details: [
			{
				label: "Hours Available",
				value: formatValue(data.HoursAvailable, 2, "Hrs"),
				tooltip: "Total elapsed shift hours.",
			},
			{
				label: "Uptime %",
				value: formatValue(data.UptimePercentage, 1, "%"),
				tooltip: "Uptime Hours / Hours Actual x 100.",
			},
			{
				label: "Avg TPH during Uptime",
				value: formatValue(data.AvgTPHDuringUptime, 1, "TPH"),
				tooltip: "Rolled Tons / Uptime Hours",
			},
		],
	},
	{
		key: "pushrate",
		title: "Push Rate",
		value: n(data.PushRateWithDelays),
		unit: "Billets",
		percentage: n(data.PushRatePct),
		subtitle: "Avg. Per Hour",
		tooltip: "Average billets per hour during the shift",
		thresholds: {
			red: data.PushRateRed,
			orange: data.PushRateOrange,
			yellow: data.PushRateYellow,
			greenSoft: data.PushRateGreenSoft,
			greenNormal: data.PushRateGreenNormal,
			greenHard: data.PushRateGreenHard,
		},
		details: [
			{
				label: "Avg. Billets/h during Uptime",
				value: formatValue(data.PushRateActual, 1, ""),
				tooltip: "Billets per uptime hour (excl. delays).",
			},
			{
				label: "Scheduled Push Rate",
				value: formatValue(data.PushRateScheduled, 1, ""),
			},
		],
	},
	{
		key: "productivity",
		title: "Productivity",
		value: n(data.ProductivityOverTarget),
		unit: "%",
		percentage: n(data.ProductivityOverTarget),
		subtitle: "Overall Productivity",
		tooltip: "Actual TPH / Target TPH x 100",
		thresholds: {
			red: data.ProductivityRed,
			orange: data.ProductivityOrange,
			yellow: data.ProductivityYellow,
			greenSoft: data.ProductivityGreenSoft,
			greenNormal: data.ProductivityGreenNormal,
			greenHard: data.ProductivityGreenHard,
		},
		details: [
			{
				label: "Actual Tons",
				value: actualTons(data),
				tooltip: actualTonsTooltip(data, "period"),
			},
			{
				label: "Target Tons",
				value: targetTons(data),
				tooltip: targetTonsTooltip(data, "period"),
			},
			{
				label: "Productivity during Uptime",
				value: formatValue(productivityDuringUptime(data), 1, "%"),
				tooltip:
					"Avg TPH during Uptime / Target TPH x 100. Pace while actually rolling, with delays excluded.",
			},
		],
	},
];

type Props = {
	title?: string;
	data: KPIData | null;
	/** When set, only renders cards whose key is in this list. */
	visible?: CardKey[];
};

export const KPISection = ({ title, data, visible }: Props) => {
	if (!data) return null;

	const all = buildCards(data);
	const cards = visible ? all.filter((c) => visible.includes(c.key)) : all;

	const colSize = 12 / cards.length;

	return (
		<Box sx={{ display: "flex", gap: 1, width: "100%", height: "100%" }}>
			{title ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						minWidth: "20px",
						maxWidth: "20px",
					}}
				>
					<Typography
						variant="subtitle2"
						sx={{
							fontWeight: 600,
							transform: "rotate(-90deg)",
							whiteSpace: "nowrap",
						}}
					>
						{title}
					</Typography>
				</Box>
			) : null}
			<Grid2 container spacing={1} sx={{ flex: 1, alignItems: "stretch" }}>
				{cards.map((c) => (
					<Grid2 key={c.key} size={{ xs: colSize }}>
						<KPICard {...c} />
					</Grid2>
				))}
			</Grid2>
		</Box>
	);
};
